/*
 * The Streaming Section's entrance.
 *
 * It lives here rather than in the section because the theme swaps #MainContent
 * on an SPA navigation, and an inline <script> inside swapped-in HTML is never
 * executed — activateInsertedScripts only re-runs scripts for the lazily
 * inserted homepage sections. A section whose reveal script had not run would
 * have kept every child at opacity 0: not a missing animation, a missing hero.
 *
 * So: loaded once from theme.liquid, and re-scanned whenever the page changes
 * underneath it. Same arrangement as od-membership-plans.js and the rest.
 *
 * The hiding is applied by this script, never by Liquid. If the file fails to
 * load the section simply appears, finished, with no animation — which is the
 * right way round for something that would otherwise be invisible.
 */
(function () {
  'use strict';

  var ARMED = 'is-armed';
  var REVEALED = 'is-revealed';
  var BOUND = 'odStreamingBound';

  function prefersReducedMotion() {
    return Boolean(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function init(section) {
    if (!section || section.dataset[BOUND] === 'true') return;
    section.dataset[BOUND] = 'true';

    var animated = section.classList.contains('streaming-section--animated');

    /* Nothing to play: show it whole and have done. */
    if (!animated || prefersReducedMotion() || !('IntersectionObserver' in window)) {
      section.classList.add(REVEALED);
      return;
    }

    /* Hidden only now, so the section is never left invisible by a script that
       did not arrive. Reading offsetWidth commits the armed state to the page
       before the revealed one is added, or the two would collapse into one
       frame and nothing would move. */
    section.classList.add(ARMED);
    void section.offsetWidth;

    /*
     * A deadline, as the editorial hero has for its own reveal.
     *
     * Everything above assumes the observer will eventually say something. If
     * it never does — a browser that delivers nothing while the tab is hidden
     * and is then restored oddly, a section parked somewhere it can never
     * intersect, an observer that simply does not run — the section would stay
     * armed, which is to say invisible. Three seconds and it shows regardless.
     */
    var settled = false;

    var reveal = function (node) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      node.classList.add(REVEALED);
    };

    var timer = window.setTimeout(function () {
      observer.disconnect();
      reveal(section);
    }, 3000);

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          observer.disconnect();
          reveal(entries[i].target);
          return;
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.2 }
    );

    observer.observe(section);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var found = scope.querySelectorAll('.streaming-section');
    for (var i = 0; i < found.length; i++) init(found[i]);
  }


  /* ------------------------------------------------------------------
   * The transition into the membership page.
   *
   * The theme's own flight sends a clone of one element to a matching one on
   * the next page — right for a collection card, wrong here: it flew a
   * full-bleed hero into a short wide band, so a whole screen of artwork
   * squashed into a letterbox. It is also mobile-only, and this wants to work
   * on a desktop.
   *
   * So: a curtain, which is the one theatrical gesture this shop has earned.
   * It rises from the foot of the screen, covers the swap, and keeps going
   * up — one continuous movement in one direction rather than a veil that
   * drops and then lifts. A single gold line leads its edge.
   *
   * Nothing here blocks the navigation. The router is already fetching on the
   * same click; this only covers what it does. If the swap never lands, the
   * curtain clears itself after a second and a half.
   * ---------------------------------------------------------------- */
  var CURTAIN_ID = 'od-streaming-curtain';
  var STYLE_ID = 'od-streaming-curtain-styles';

  function ensureCurtainStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      '#' + CURTAIN_ID + '{',
      'position:fixed;inset:0;z-index:2147483000;pointer-events:none;',
      'transform:translate3d(0,100%,0);',
      'background:linear-gradient(180deg,#2b0052 0%,#22003f 55%,#1a0030 100%);',
      'will-change:transform;}',
      '#' + CURTAIN_ID + '::before{content:"";position:absolute;left:0;right:0;top:0;height:2px;',
      'background:linear-gradient(90deg,rgba(215,170,54,0) 0%,#f6e3a8 18%,#d7aa36 50%,#f6e3a8 82%,rgba(215,170,54,0) 100%);',
      'box-shadow:0 0 18px rgba(215,170,54,.55);}',
      '#' + CURTAIN_ID + '.is-closing{transform:translate3d(0,0,0);',
      'transition:transform 340ms cubic-bezier(.32,.94,.6,1);}',
      '#' + CURTAIN_ID + '.is-opening{transform:translate3d(0,-100%,0);',
      'transition:transform 620ms cubic-bezier(.16,1,.3,1);}',
      '@media (prefers-reduced-motion: reduce){#' + CURTAIN_ID + '{display:none;}}'
    ].join('');
    document.head.appendChild(style);
  }

  function raiseCurtain() {
    if (prefersReducedMotion()) return;

    ensureCurtainStyles();

    var existing = document.getElementById(CURTAIN_ID);
    if (existing) existing.remove();

    var curtain = document.createElement('div');
    curtain.id = CURTAIN_ID;
    curtain.setAttribute('aria-hidden', 'true');
    document.body.appendChild(curtain);

    /* Committed to the page before the class that moves it, or the browser
       folds both into one frame and there is nothing to see. */
    void curtain.offsetWidth;
    curtain.classList.add('is-closing');

    var finished = false;

    var lift = function () {
      if (finished) return;
      finished = true;
      document.removeEventListener('od:main-content-replaced', onSwap);
      window.clearTimeout(failsafe);

      /* A held beat at full cover, so the new page is in place behind it. */
      window.setTimeout(function () {
        curtain.classList.remove('is-closing');
        curtain.classList.add('is-opening');
        window.setTimeout(function () { curtain.remove(); }, 700);
      }, 90);
    };

    var onSwap = function () { lift(); };
    document.addEventListener('od:main-content-replaced', onSwap, { once: true });

    /* Nothing arrived: clear rather than sit over the shop. */
    var failsafe = window.setTimeout(lift, 1500);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target && event.target.closest
      ? event.target.closest('.streaming-section__button, .streaming-section__link, [data-od-curtain]')
      : null;
    if (!trigger) return;
    if (event.defaultPrevented && !trigger.hasAttribute('x-element-transition-trigger')) return;
    /* data-od-curtain is the opt-in for a link outside this section that should
       arrive the same way. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (trigger.getAttribute('target') === '_blank') return;

    var href = trigger.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return;

    /* Only for a link the router will handle in place. A full page load paints
       the next page itself and a curtain would simply be thrown away. */
    try {
      var url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname + url.search === window.location.pathname + window.location.search) return;
    } catch (error) {
      return;
    }

    raiseCurtain();
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); }, { once: true });
  } else {
    scan(document);
  }

  /* The theme announces its own swaps. */
  document.addEventListener('od:main-content-replaced', function () { scan(document); });

  /* The theme editor rebuilds a section on every change. */
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target || document);
  });

  /*
   * And a watcher for everything else: a section inserted by any path the two
   * events above do not cover. Cheap, because init() leaves a mark and returns
   * immediately on anything it has already seen.
   */
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (!node || node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('streaming-section')) init(node);
          else if (node.querySelector) scan(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
